import { Capacitor } from "@capacitor/core";
import { appVersion, isMobile } from "./mobile";
import { isNetworkError, NetworkError } from "./networkError";
import { ACTIVE_ORG_REF_LOCAL_STORAGE_KEY } from "./organizationStorage";

const apiUrlStaging = (window as any).env?.apiUrlStaging;
const apiUrlProd = (window as any).env?.apiUrlProd;
const apiRootProd = apiUrlProd ? new URL(apiUrlProd) : undefined;
const apiRootStaging = apiUrlStaging ? new URL(apiUrlStaging) : undefined;

const apiRootLocalStorageKey = "apiRoot";
export const ENVIRONMENT_PROD = "prod" as const;
export const ENVIRONMENT_STAGING = "staging" as const;

let environment =
  localStorage.getItem(apiRootLocalStorageKey) ?? ENVIRONMENT_PROD;

export const getEnvironment = () => {
  return environment;
};

export const setApiEnvironment = (
  newEnvironment: typeof ENVIRONMENT_STAGING | typeof ENVIRONMENT_PROD
) => {
  environment = newEnvironment;
  localStorage.setItem(apiRootLocalStorageKey, newEnvironment);
};

let organization: string | undefined =
  localStorage.getItem(ACTIVE_ORG_REF_LOCAL_STORAGE_KEY) ?? undefined;

export const setOrganizationRef = (ref: string | undefined) => {
  organization = ref;
  if (ref) {
    localStorage.setItem(ACTIVE_ORG_REF_LOCAL_STORAGE_KEY, ref);
  } else {
    localStorage.removeItem(ACTIVE_ORG_REF_LOCAL_STORAGE_KEY);
  }
};

export const getApiRoot = () => {
  if (!isMobile) {
    return "";
  }

  if (!apiRootProd || !apiRootStaging) {
    throw new Error(
      "VITE_API_ROOT_STAGING and VITE_API_ROOT_PROD must be set on mobile"
    );
  }
  return environment === "staging" ? apiRootStaging.origin : apiRootProd.origin;
};

// In the browser there's no semver: send the server's version id from when
// this tab loaded index.html, so requests from a tab opened before the latest
// deploy can be spotted (it differs from the current /api/version). Still a
// placeholder under the Vite dev server.
const webAppVersion: string | undefined = (window as any).env?.appVersion;
const webVersionHeaders: Record<string, string> =
  webAppVersion && webAppVersion !== "__APP_VERSION__"
    ? { "x-app-version": webAppVersion, "x-app-platform": "web" }
    : {};

export const apiFetch = async (input: RequestInfo, init?: RequestInit) => {
  const hasQuery = (input as string).includes("?");
  const url = isMobile
    ? `${getApiRoot()}${(input as string).startsWith("/drivers-api") ? `/native-app${input}` : input}${hasQuery ? `&loginOrgRef=${organization}` : getOrgRefSingleQueryParam()}`
    : input;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  const mobileHeaders: Record<string, string> = isMobile
    ? {
        "x-app-version": appVersion ?? "",
        "x-app-platform": Capacitor.getPlatform(),
      }
    : webVersionHeaders;

  try {
    return await fetch(url, {
      ...init,
      headers: {
        ...mobileHeaders,
        ...(init?.headers as Record<string, string>),
      },
      signal: init?.signal || controller.signal,
    });
  } catch (e) {
    if (isNetworkError(e)) {
      throw new NetworkError(
        e instanceof Error ? e.message : "Network request failed",
        e
      );
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getOrgRefSingleQueryParam = () => `?loginOrgRef=${organization}`;
